import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, type AdminCategory } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const ICONS = [
    "Apple", "ShoppingBasket", "Package", "Cookie", "Coffee", "Utensils",
    "Sparkles", "Baby", "PawPrint", "Cigarette", "Wine", "Home", "Shirt",
];

const GRADIENTS = [
    "from-brand/10 to-accentbrand/10",
    "from-sky-500/10 to-cyan-400/10",
    "from-amber-500/10 to-orange-400/10",
    "from-violet-500/10 to-fuchsia-400/10",
    "from-rose-500/10 to-pink-400/10",
    "from-emerald-500/10 to-lime-400/10",
];

type FormState = {
    id?: string;
    name: string;
    slug: string;
    subtitle: string;
    description: string;
    icon: string;
    gradient: string;
    order: number;
    is_active: boolean;
};

function emptyForm(): FormState {
    return {
        name: "",
        slug: "",
        subtitle: "",
        description: "",
        icon: "Package",
        gradient: GRADIENTS[0],
        order: 100,
        is_active: true,
    };
}

export default function AdminCategories() {
    const qc = useQueryClient();
    const { data = [], isLoading } = useQuery({
        queryKey: ["admin", "categories"],
        queryFn: adminApi.listCategories,
    });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openNew = () => {
        setForm(emptyForm());
        setError(null);
        setDialogOpen(true);
    };

    const openEdit = (c: AdminCategory) => {
        setForm({
            id: c.id,
            name: c.name,
            slug: c.slug,
            subtitle: c.subtitle,
            description: c.description,
            icon: c.icon,
            gradient: c.gradient,
            order: c.order,
            is_active: c.is_active,
        });
        setError(null);
        setDialogOpen(true);
    };

    const delMut = useMutation({
        mutationFn: (id: string) => adminApi.deleteCategory(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "categories"] });
            toast.success("Category deleted");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't delete category")),
    });

    const onSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const payload = { ...form };
            if (form.id) {
                await adminApi.updateCategory(form.id, payload);
                toast.success("Category updated");
            } else {
                await adminApi.createCategory(payload);
                toast.success("Category created");
            }
            qc.invalidateQueries({ queryKey: ["admin", "categories"] });
            setDialogOpen(false);
        } catch (err) {
            setError(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-categories-page" className="space-y-5">
            <div className="flex items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">Categories</h1>
                    <p className="text-sm text-muted-foreground">
                        {data.length} categories
                    </p>
                </div>
                <button
                    onClick={openNew}
                    data-testid="admin-category-new"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                >
                    <Plus className="h-4 w-4" /> New category
                </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : data.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No categories yet.</p>
                ) : (
                    data.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`admin-category-card-${c.id}`}
                            className={`rounded-2xl border border-border/70 bg-gradient-to-br ${c.gradient} p-4 shadow-soft`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h3 className="font-bold">{c.name}</h3>
                                    <p className="text-xs text-muted-foreground">/{c.slug}</p>
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(c)}
                                        data-testid={`admin-category-edit-${c.id}`}
                                        className="grid h-8 w-8 place-items-center rounded-lg bg-white/60 hover:bg-white"
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (window.confirm(`Delete "${c.name}"?`)) delMut.mutate(c.id);
                                        }}
                                        data-testid={`admin-category-delete-${c.id}`}
                                        className="grid h-8 w-8 place-items-center rounded-lg bg-white/60 text-error hover:bg-white"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            {c.subtitle ? (
                                <p className="mt-2 text-sm text-foreground/70">{c.subtitle}</p>
                            ) : null}
                            <div className="mt-3 flex items-center gap-2 text-xs">
                                <span className="rounded-full bg-white/50 px-2 py-0.5">order {c.order}</span>
                                <span className={`rounded-full px-2 py-0.5 ${c.is_active ? "bg-emerald-500/20 text-emerald-800" : "bg-slate-500/20 text-slate-700"}`}>
                                    {c.is_active ? "Live" : "Hidden"}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="admin-category-dialog" className="sm:max-w-lg">
                    <form onSubmit={onSave}>
                        <DialogHeader>
                            <DialogTitle>{form.id ? "Edit category" : "New category"}</DialogTitle>
                        </DialogHeader>
                        <div className="mt-4 space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Name *</Label>
                                    <Input
                                        data-testid="admin-category-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        required
                                        minLength={2}
                                    />
                                </div>
                                <div>
                                    <Label>Slug</Label>
                                    <Input
                                        value={form.slug}
                                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                        placeholder="auto"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Subtitle</Label>
                                <Input
                                    value={form.subtitle}
                                    onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <textarea
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <div>
                                    <Label>Icon</Label>
                                    <select
                                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                        value={form.icon}
                                        onChange={(e) => setForm({ ...form, icon: e.target.value })}
                                    >
                                        {ICONS.map((i) => (
                                            <option key={i}>{i}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label>Gradient</Label>
                                    <select
                                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                        value={form.gradient}
                                        onChange={(e) => setForm({ ...form, gradient: e.target.value })}
                                    >
                                        {GRADIENTS.map((g) => (
                                            <option key={g} value={g}>
                                                {g.replace("from-", "").replace(" to-", " → ")}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label>Order</Label>
                                    <Input
                                        type="number"
                                        value={form.order}
                                        onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                />
                                Live (visible to shoppers)
                            </label>
                            {error ? (
                                <p role="alert" data-testid="admin-category-error" className="text-xs font-medium text-error">
                                    {error}
                                </p>
                            ) : null}
                        </div>
                        <DialogFooter className="mt-4 gap-2">
                            <button
                                type="button"
                                onClick={() => setDialogOpen(false)}
                                className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <SubmitButton
                                type="submit"
                                fullWidth={false}
                                loading={saving}
                                data-testid="admin-category-save"
                            >
                                Save
                            </SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
